import { EnhancedSitecoreContextResourceType } from '../types/types';

export const PERSONALIZE_EMBEDDED_CODE = 'personalizeembedded';
export const SEARCH_EMBEDDED_CODE = 'searchembedded';

export const USER_SEARCH_RESOURCE_ID = 'sitecoreSearchResource';

const EMBEDDED_RESOURCE_PRODUCT_CODES = [
  PERSONALIZE_EMBEDDED_CODE,
  SEARCH_EMBEDDED_CODE,
];

export const isEmbeddedResourceProductCode = (
  tenantProductCode?: string,
): boolean => {
  if (!tenantProductCode) return false;

  return EMBEDDED_RESOURCE_PRODUCT_CODES.includes(
    tenantProductCode.toLowerCase(),
  );
};

export const isEmbeddedResource = (
  resource: Pick<
    EnhancedSitecoreContextResourceType,
    'tenantProductCode' | 'type'
  >,
): boolean => {
  const tenantProductCode = resource.tenantProductCode?.toLowerCase();
  const resourceType = resource.type?.toLowerCase();

  if (isEmbeddedResourceProductCode(tenantProductCode)) {
    return true;
  }

  // Personalize can come back as XMCloud when it is embedded.
  return resourceType === 'personalize' && tenantProductCode === 'xmcloud';
};

const getEmbeddedResourceIds = (
  scopedContextData: EnhancedSitecoreContextResourceType[],
): string[] =>
  scopedContextData
    .filter((resource) => isEmbeddedResource(resource))
    .map((resource) => resource.id);

const hasSiteOrEdgeResourcesSelected = (
  allowedResourceIds: string[],
  scopedContextData: EnhancedSitecoreContextResourceType[],
): boolean =>
  scopedContextData.some(
    (resource) =>
      (resource.type === 'site' || resource.type === 'edge') &&
      allowedResourceIds.includes(resource.id),
  );

const dedupe = (resourceIds: string[]): string[] => [...new Set(resourceIds)];

export const addEmbeddedResourcesForCreation = (
  allowedResourceIds: string[],
  scopedContextData: EnhancedSitecoreContextResourceType[],
): string[] => {
  if (!hasSiteOrEdgeResourcesSelected(allowedResourceIds, scopedContextData)) {
    return allowedResourceIds;
  }

  const embeddedResourceIds = getEmbeddedResourceIds(scopedContextData);
  return dedupe([...allowedResourceIds, ...embeddedResourceIds]);
};

export const normalizeEmbeddedResourcesForEdit = (
  allowedResourceIds: string[],
  scopedContextData: EnhancedSitecoreContextResourceType[],
): string[] => {
  const embeddedResourceIds = getEmbeddedResourceIds(scopedContextData);
  const userSelectedIds = allowedResourceIds.filter(
    (resourceId) => !embeddedResourceIds.includes(resourceId),
  );

  if (!hasSiteOrEdgeResourcesSelected(userSelectedIds, scopedContextData)) {
    return userSelectedIds;
  }

  return dedupe([...userSelectedIds, ...embeddedResourceIds]);
};
